import {
    GetTrackedProjectsQuery,
    InQueueScenarioFieldsFragment,
    SubscribeToInQueueScenariosDocument,
    SubscribeToInQueueScenariosSubscription,
    SubscribeToInQueueScenariosSubscriptionVariables,
    useGetClusterProjectsQuery,
    useGetInQueueScenariosQuery,
    useGetSpeculativeProjectsQuery,
    useGetTrackedProjectsQuery
} from "generated/graphql";
import {
    isSameCluster,
    LegacyClusterInfo,
    shouldShowInCluster,
    StudyPhase
} from "in_queue/types/clusterType";
import {
    convertHasuraProjectsToProjects,
    convertHasuraSpeculativeProjectsToSpeculativeProjects,
    getTrackedProjects,
    InQueueSubscription,
    ProjectType,
    QueueProject,
    SpeculativeProject,
    TrackedProject,
    TrackedQueueProject
} from "in_queue/types/projectType";
import {
    convertHasuraInQueueScenariosToProjectScenarioMapping,
    ProjectScenarioMapping,
    ScenarioMetadata
} from "in_queue/types/scenarioType";
import { flatten, keyBy, mapValues } from "lodash";
import {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useMemo
} from "react";
import { Loading, mapLoadingState, zipLoadingStates } from "types/loadingType";
import { useCluster, useClusters } from "./ClusterContext";
import { useSubscriptions } from "./InQueueSubscriptionsContext";

interface ProjectDataContextValue {
    allProjects: Loading<QueueProject[]>;
    trackedProjects: Loading<TrackedProject[]>;
    scenarioMapping: Loading<ProjectScenarioMapping>;
    projectIdToClusterMapping: Loading<Record<string, LegacyClusterInfo>>;

    // TODO: move these to a separate context?
    speculativeProjects: Loading<SpeculativeProject[]>;
}

export const ProjectDataContext = createContext<ProjectDataContextValue>({
    allProjects: "loading",
    trackedProjects: "loading",
    scenarioMapping: "loading",
    projectIdToClusterMapping: "loading",
    speculativeProjects: "loading"
});

export const ProjectDataProvider: React.FC<PropsWithChildren<unknown>> = ({
    children
}: PropsWithChildren<unknown>) => {
    const { id: clusterId } = useCluster();
    const clusters = useClusters();

    const { data: hasuraClusterProjects, loading } = useGetClusterProjectsQuery(
        {
            variables: { clusterId: clusterId }
        }
    );

    let clusterProjects: Loading<QueueProject[]> = "loading";
    const projectIdToClusterMapping: Record<string, LegacyClusterInfo> = {};
    if (!loading && hasuraClusterProjects) {
        clusterProjects = convertHasuraProjectsToProjects(
            hasuraClusterProjects
        );

        clusterProjects.forEach((project: QueueProject) => {
            projectIdToClusterMapping[project.projectId] = project.cluster;
        });
    }

    const hasuraScenarios = useHasuraInQueueScenarios();

    const hasuraTrackedProjects = useHasuraTrackedProjects();
    const subscriptions = useSubscriptions();

    const subscriptionIds =
        subscriptions === "loading"
            ? []
            : subscriptions.map((sub) => sub.subscriptionId);

    const shouldSkipSpeculativeProjects =
        subscriptions === "loading" || subscriptionIds.length === 0;

    const {
        data: hasuraSpeculativeProjects,
        loading: speculativeProjectsLoading
    } = useGetSpeculativeProjectsQuery({
        variables: { subscriptionIds },
        skip: shouldSkipSpeculativeProjects
    });
    let speculativeProjects: Loading<SpeculativeProject[]> = "loading";
    if (!speculativeProjectsLoading && hasuraSpeculativeProjects) {
        speculativeProjects =
            convertHasuraSpeculativeProjectsToSpeculativeProjects(
                hasuraSpeculativeProjects,
                clusters
            );

        speculativeProjects.forEach((project: SpeculativeProject) => {
            projectIdToClusterMapping[project.projectId] = project.cluster;
        });
    }

    const trackedProjects = getTrackedProjects({
        hasuraData: hasuraTrackedProjects,
        speculativeProjects,
        queueProjects: clusterProjects,
        subscriptions
    });

    const scenarioMapping = mapLoadingState(hasuraScenarios, (scenarios) =>
        convertHasuraInQueueScenariosToProjectScenarioMapping(
            scenarios,
            trackedProjects,
            speculativeProjects,
            subscriptions
        )
    );

    const value = {
        allProjects: clusterProjects,
        trackedProjects,
        scenarioMapping,
        projectIdToClusterMapping,
        speculativeProjects
    };
    return (
        <ProjectDataContext.Provider value={value}>
            {children}
        </ProjectDataContext.Provider>
    );
};

// Note: Here, "Subscription" refers to Hasura's notion of a subscription, not ours
const useHasuraInQueueScenarios = (): Loading<
    ReadonlyArray<InQueueScenarioFieldsFragment>
> => {
    const { data, loading, subscribeToMore } = useGetInQueueScenariosQuery();

    useEffect(
        () =>
            subscribeToMore<
                SubscribeToInQueueScenariosSubscription,
                SubscribeToInQueueScenariosSubscriptionVariables
            >({
                document: SubscribeToInQueueScenariosDocument,
                updateQuery: (previous, { subscriptionData }) =>
                    subscriptionData.data
                        ? { scenarios: subscriptionData.data.scenarios }
                        : previous
            }),
        [subscribeToMore]
    );

    if (loading || !data) {
        return "loading";
    }
    return data.scenarios;
};

const useHasuraTrackedProjects = (): Loading<GetTrackedProjectsQuery> => {
    const { data: trackedProjects, loading } = useGetTrackedProjectsQuery({});
    if (loading || !trackedProjects) {
        return "loading";
    }
    return trackedProjects;
};

export const useAllQueueProjects = (): Loading<QueueProject[]> => {
    return useContext(ProjectDataContext).allProjects;
};

export const useAllEditableQueueProjects = (): Loading<QueueProject[]> => {
    const allProjectsInCluster = useAllQueueProjects();
    return mapLoadingState(allProjectsInCluster, (projects: QueueProject[]) =>
        projects.filter((project: QueueProject) => project.isEditable)
    );
};

export const useNonTrackedQueueProjectsInCluster = (): Loading<
    QueueProject[]
> => {
    const allProjects = useAllQueueProjects();
    const trackedProjects = useTrackedProjectsInCluster();
    const cluster = useCluster();

    return zipLoadingStates(
        allProjects,
        trackedProjects,
        (projects, tracked) => {
            const trackedProjectIds = new Set(
                tracked.map((project) => project.projectId)
            );
            return projects.filter(
                (project) =>
                    !trackedProjectIds.has(project.projectId) &&
                    shouldShowInCluster(project.cluster, cluster)
            );
        }
    );
};

export const useSubscriptionForCluster = (): Loading<
    InQueueSubscription | undefined
> => {
    const subscriptions = useSubscriptions();
    const cluster = useCluster();

    return mapLoadingState(subscriptions, (subscriptions) =>
        subscriptions.find((sub) => isSameCluster(sub.clusterInfo, cluster))
    );
};

export const useQueueProject = ({
    projectId
}: {
    projectId: string | undefined;
}): Loading<QueueProject | undefined> => {
    const allProjects = useAllQueueProjects();
    return mapLoadingState(allProjects, (projects) =>
        projects.find((project) => project.projectId === projectId)
    );
};

export const useTrackedProjects = (): Loading<TrackedProject[]> => {
    const context = useContext(ProjectDataContext);
    return context.trackedProjects;
};

export const useTrackedProjectsInCluster = (): Loading<TrackedProject[]> => {
    const trackedProjects = useTrackedProjects();
    const cluster = useCluster();

    return useMemo(
        () =>
            mapLoadingState(trackedProjects, (projects) => {
                return projects.filter((project) =>
                    shouldShowInCluster(project.cluster, cluster)
                );
            }),
        [trackedProjects, cluster]
    );
};

export const useTrackedQueueProjects = (): Loading<TrackedQueueProject[]> => {
    const trackedProjects = useTrackedProjects();

    return mapLoadingState(trackedProjects, (projects) => {
        return projects.filter(
            (project) => project.type === ProjectType.QUEUE_PROJECT
        ) as TrackedQueueProject[];
    });
};

export const useTrackedQueueProjectsInCluster = (): Loading<
    TrackedQueueProject[]
> => {
    const trackedQueueProjects = useTrackedQueueProjects();
    const cluster = useCluster();

    return mapLoadingState(trackedQueueProjects, (projects) => {
        return projects.filter((project) =>
            shouldShowInCluster(project.cluster, cluster)
        );
    });
};

export const useTrackedProject = ({
    projectId,
    phase
}: {
    projectId: string | undefined;
    phase: StudyPhase;
}): Loading<TrackedProject | undefined> => {
    const trackedProjects = useTrackedProjects();
    return mapLoadingState(trackedProjects, (projects) =>
        projects.find(
            (project) =>
                project.projectId === projectId &&
                project.cluster.studyPhase === phase
        )
    );
};

export const useSpeculativeProjects = (): Loading<SpeculativeProject[]> => {
    const context = useContext(ProjectDataContext);
    return context.speculativeProjects;
};

export const useSpeculativeProjectsInCluster = (): Loading<
    SpeculativeProject[]
> => {
    const speculativeProjects = useSpeculativeProjects();
    const cluster = useCluster();

    return mapLoadingState(speculativeProjects, (projects) => {
        return projects.filter((project) =>
            isSameCluster(project.cluster, cluster)
        );
    });
};

export const useAllAvailableProjects = (): Loading<
    (QueueProject | SpeculativeProject)[]
> => {
    // These are all the projects that can be included in a scenario either as a
    // project assumption (QueueProject) or a project addition (SpeculativeProject)
    const editableProjects = useAllEditableQueueProjects();
    const speculativeProjects = useSpeculativeProjectsInCluster();

    return editableProjects !== "loading" && speculativeProjects !== "loading"
        ? [...editableProjects, ...speculativeProjects]
        : "loading";
};

export const useProjectFuelTypes = (): Loading<{
    [projectId: string]: string;
}> => {
    const queueProjects = useAllQueueProjects();
    const speculativeProjects = useSpeculativeProjects();
    return zipLoadingStates(
        queueProjects,
        speculativeProjects,
        (qp: QueueProject[], sp: SpeculativeProject[]) => {
            return mapValues(
                keyBy([...qp, ...sp], "projectId"),
                (project) => project.fuelType
            );
        }
    );
};

export const useProjectScenarioMapping =
    (): Loading<ProjectScenarioMapping> => {
        const context = useContext(ProjectDataContext);
        return context.scenarioMapping;
    };

export const useProjectScenarios = ({
    projectId,
    phase
}: {
    projectId: string | undefined;
    phase: StudyPhase;
}): Loading<ScenarioMetadata[]> => {
    const scenarioMapping = useProjectScenarioMapping();
    return mapLoadingState(scenarioMapping, (mapping) =>
        projectId && mapping[projectId] && mapping[projectId][phase]
            ? mapping[projectId][phase]
            : []
    );
};

export const useScenarioMetadata = (
    projectId: string | undefined,
    scenarioId: string | undefined
): Loading<ScenarioMetadata | undefined> => {
    const projectScenarioMapping = useProjectScenarioMapping();

    if (projectId === undefined || scenarioId === undefined) {
        return undefined;
    }
    return mapLoadingState(projectScenarioMapping, (projectScenarioMapping) => {
        const phaseToScenarioMapping = projectScenarioMapping[projectId] ?? {};
        const allProjectScenarios = flatten(
            Object.values(phaseToScenarioMapping)
        );

        return allProjectScenarios.find(
            (s: ScenarioMetadata) => s.scenarioId === scenarioId
        );
    });
};

export const useScenariosInCluster = (): Loading<ScenarioMetadata[]> => {
    const scenarioMapping = useProjectScenarioMapping();
    const trackedProjects = useTrackedQueueProjectsInCluster();
    const cluster = useCluster();

    return zipLoadingStates(
        scenarioMapping,
        trackedProjects,
        (
            scenarioMapping: ProjectScenarioMapping,
            trackedProjects: TrackedProject[]
        ) => {
            const scenarios: { [scenarioId: string]: ScenarioMetadata } = {};

            trackedProjects.forEach((tp: TrackedProject) => {
                if (
                    scenarioMapping[tp.projectId] &&
                    scenarioMapping[tp.projectId][cluster.studyPhase]
                ) {
                    scenarioMapping[tp.projectId][cluster.studyPhase].forEach(
                        (scenario: ScenarioMetadata) => {
                            scenarios[scenario.scenarioId] = scenario;
                        }
                    );
                }
            });

            return Object.values(scenarios);
        }
    );
};

export const useProjectIdToClusterMapping = (): Loading<
    Record<string, LegacyClusterInfo>
> => {
    const context = useContext(ProjectDataContext);
    return context.projectIdToClusterMapping;
};

export const useIncludesCrossClusterQueueProjects = (): boolean => {
    const context = useContext(ProjectDataContext);
    const projects = context.allProjects;

    if (projects === "loading") {
        return false;
    }

    const clusters = new Set(
        projects.map(
            (project: QueueProject) =>
                `${project.cluster.studyCycle}_${project.cluster.studyGroup}`
        )
    );
    return clusters.size > 1;
};
